import { api, ApiError } from '@/lib/api';
import type {
  User,
  UsersFilters,
  UsersResponse,
  V3UsersResponse,
  Organisation,
  AccountOption,
  UpdateUserInput,
  CreateUserInput,
  NewUserOptions,
  UpdateOrganisationProfileInput,
  SessionLogEntry,
  SecurityLogResponse,
  Role,
  UpdateUserSecurityStateInput,
} from './types';

// =============================================================================
// Users API Functions
// All reads and writes are PostgreSQL-native via the V4 /v4/users endpoints.
// There is intentionally NO MongoDB or V3 fallback path in this module —
// see apps/rest_server/routes/v4/users.js for the PG-native CRUD surface.
// =============================================================================

/**
 * Get users with optional filtering, sorting, and pagination
 * Uses the PostgreSQL-backed V4 users endpoint.
 */
export async function getUsers(filters: UsersFilters = {}): Promise<UsersResponse> {
  const queryParams: Record<string, string | number | string[]> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'email',
    sortDirection: filters.sortDirection ?? 1,
    enabled: filters.enabled ?? 'all',
  };

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add organisation filter
  if (filters.organisation) {
    queryParams.organisation = filters.organisation;
  }

  // employment_type accepts a CSV (e.g. 'employee' or 'employee,contractor')
  // and is forwarded as-is to the V4 backend (see UsersV4DAO.getMany).
  if (filters.employment_type) {
    queryParams.employment_type = filters.employment_type;
  }

  const response = await api.get<UsersResponse>('/v4/users', queryParams);

  return {
    users: response.users || [],
    pageNumber: response.pageNumber ?? filters.pageNumber ?? 0,
    pageLength: response.pageLength ?? filters.pageLength ?? 25,
    total: response.total ?? 0,
  };
}

/**
 * PG UUID regex — the only id format users/api.ts accepts. Mongo ObjectIds
 * are intentionally NOT supported anywhere in user management.
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertPgId(id: string): void {
  if (!UUID_REGEX.test(id)) {
    throw new ApiError('Invalid user id — expected PG UUID', 400);
  }
}

/**
 * Get a single user by PG UUID. Hits GET /v4/users?id=<pgId> against the
 * PostgreSQL-backed V4 endpoint.
 */
export async function getUser(pgId: string): Promise<User> {
  return getUserByPgId(pgId);
}

/**
 * Alias kept for legacy import sites. Same PG-only behaviour as getUser.
 */
export async function getUserReadModel(pgId: string): Promise<User> {
  return getUserByPgId(pgId);
}

/**
 * PG-only single-user fetch. Filters by PG UUID — there is no MongoDB
 * fallback path in V4 user management.
 */
export async function getUserByPgId(pgId: string): Promise<User> {
  assertPgId(pgId);
  const response = await api.get<UsersResponse>('/v4/users', { id: pgId, pageLength: 1 });
  const user = response.users?.[0];
  if (!user) throw new ApiError('User not found in PostgreSQL', 404);
  return user;
}

/**
 * Update a user — PG-native. Hits PUT /v4/users/:pgId/profile. Backend
 * accepts both the self-edit profile subset AND admin fields; the
 * web_api_server proxy enforces field-level authorization (only
 * full_admin / director can set admin fields).
 */
export async function updateUser(pgId: string, data: UpdateUserInput): Promise<User> {
  assertPgId(pgId);
  const response = await api.put<{ user: User }>(`/v4/users/${pgId}/profile`, data);
  return response.user;
}

/**
 * Self-service profile update — same PG endpoint as `updateUser`, but the
 * input type is narrowed to the fields a non-admin can legally set. Kept as
 * a separate export so callers like ProfileModal advertise their intent.
 */
export async function updateUserProfileByPgId(
  pgId: string,
  data: Pick<
    UpdateUserInput,
    | 'firstname' | 'lastname' | 'phone' | 'extension' | 'mobile'
    | 'position' | 'company' | 'language'
    | 'linkedin' | 'linkedin_email' | 'linkedin_password' | 'profile_photo_url'
  >,
): Promise<User> {
  return updateUser(pgId, data as UpdateUserInput);
}

/**
 * Create a new user — PG-native. Hits POST /v4/users (rest_server inserts a
 * row in the `users` table and hashes `password` into `pg_hashword` via
 * argon2). Proxy is gated by the users.create permission.
 *
 * The legacy `organisation` + `admin` + `tags` + `description` fields on
 * CreateUserInput map into the per-org profile that callers should set
 * afterwards via `updateOrganisationProfile` once the user row exists.
 */
export async function createUser(data: CreateUserInput): Promise<User> {
  // Split the org-membership bits off — they go into a follow-up call so the
  // server-side row stays single-shot and the per-org profile lives in its
  // own jsonb entry.
  const { organisation, admin, tags, description, usergroups, sensorgroups, ...rest } = data;
  const response = await api.post<{ user: User }>('/v4/users', rest);
  const user = response.user;

  if (organisation) {
    await updateOrganisationProfile(user.id ?? user.pg_id ?? '', organisation, {
      admin,
      usergroups,
      sensorgroups,
      tags,
      description,
    });
  }
  return user;
}

/**
 * Check if an email is already in use. PG-only.
 */
export async function checkEmailUnique(email: string): Promise<boolean> {
  const response = await api.get<V3UsersResponse>('/v4/users', {
    email,
    pageLength: 1,
  });
  return !response.users || response.users.length === 0;
}

/**
 * Check if a username is already in use. PG-only — uses GET /v4/users with
 * a username search. The legacy `/v4/legacy/v2/users/check-unique` route
 * was Mongo-backed and is no longer used.
 */
export async function checkUsernameUnique(username: string): Promise<boolean> {
  const response = await api.get<UsersResponse>('/v4/users', {
    search: username,
    pageLength: 5,
  });
  const exact = (response.users || []).find((u) => u.username === username);
  return !exact;
}

/**
 * Get available usergroups and sensorgroups for an organisation. PG-only
 * via /v4/accounts when an accountId is supplied. The legacy
 * /v4/legacy/v2/users/new-options route was Mongo-backed and is no longer
 * called — callers without an account context get an empty options set.
 */
export async function getNewUserOptions(
  _organisationId: string,
  accountId?: string,
): Promise<NewUserOptions> {
  if (accountId) {
    return api.get<NewUserOptions>(`/v4/accounts/${accountId}/user-options`);
  }
  return { usergroups: [], sensorgroups: [] };
}

/**
 * Update a user's organisation profile (admin level, usergroups, sensorgroups,
 * tags, description). PG-native: writes to the `organisation_profiles` jsonb
 * column via PUT /v4/users/:pgId/organisations/:organisationId.
 */
export async function updateOrganisationProfile(
  pgId: string,
  organisationId: string,
  data: UpdateOrganisationProfileInput,
): Promise<void> {
  assertPgId(pgId);
  await api.put(`/v4/users/${pgId}/organisations/${organisationId}`, data);
}

/**
 * Get session log for a user. PG-native via a dedicated session-log endpoint
 * (the legacy V2 sessions list is no longer consulted). If the V4 endpoint
 * is not yet implemented the caller receives an empty list rather than
 * proxying to Mongo.
 */
export async function getUserSessions(pgId: string): Promise<SessionLogEntry[]> {
  assertPgId(pgId);
  try {
    return await api.get<SessionLogEntry[]>(`/v4/users/${pgId}/sessions`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

export async function getUserSecurityLog(userId: string): Promise<SecurityLogResponse> {
  return api.get<SecurityLogResponse>(`/v4/users/${userId}/security-log`, {
    pageNumber: 0,
    pageLength: 50,
    _: Date.now(),
  });
}

// =============================================================================
// Organisations API Functions
// Used by OrganisationsFilterCombobox and selected-label lookups
// =============================================================================

/**
 * Search organisations with pagination.
 * Backed by V4/Postgres.
 */
export async function searchOrganisationsPaginated({
  search,
  pageNumber,
  pageLength,
}: {
  search: string;
  pageNumber: number;
  pageLength: number;
}): Promise<{
  organisations: Organisation[];
  pageNumber: number;
  pageLength: number;
  total: number;
}> {
  const response = await api.get<{
    organisations: Array<{
      id: string;
      mongoId: string;
      name: string;
      accountNumber?: string | null;
    }>;
    pageNumber: number;
    pageLength: number;
    total: number;
  }>('/v4/organisations', { search, pageNumber, pageLength });

  return {
    organisations: (response.organisations || []).map((organisation) => ({
      _id: organisation.mongoId,
      name: organisation.name,
      accountNumber: organisation.accountNumber ?? null,
      enabled: true,
    })),
    pageNumber: response.pageNumber ?? pageNumber,
    pageLength: response.pageLength ?? pageLength,
    total: response.total ?? 0,
  };
}

/**
 * Look up a single organisation by its MongoDB ObjectId.
 * Used to resolve a selected org's label when it isn't in the currently-loaded pages.
 */
export async function getOrganisationByMongoId(
  mongoId: string,
): Promise<Organisation | null> {
  const response = await api.get<{
    organisation: {
      id: string;
      mongoId: string;
      name: string;
      accountNumber?: string | null;
    } | null;
  }>(`/v4/organisations/${mongoId}`);

  if (!response.organisation) return null;

  return {
    _id: response.organisation.mongoId,
    name: response.organisation.name,
    accountNumber: response.organisation.accountNumber ?? null,
    enabled: true,
  };
}

/**
 * Search PostgreSQL accounts for the create-user account picker.
 * Returns the MongoDB organisation ID needed by the legacy create-user API.
 */
export async function searchAccounts(query: string): Promise<AccountOption[]> {
  const response = await api.get<{
    accounts: Array<{
      id: string;
      name: string;
      accountNumber: string;
      mongoOrganisationId?: string | null;
    }>;
  }>(
    '/v4/accounts',
    {
      search: query,
      pageNumber: 0,
      pageLength: 25,
    },
  );

  return (response.accounts || [])
    .filter((account): account is {
      id: string;
      name: string;
      accountNumber: string;
      mongoOrganisationId: string;
    } => Boolean(account.mongoOrganisationId))
    .map((account) => ({
      id: account.id,
      name: account.name,
      accountNumber: account.accountNumber,
      mongoOrganisationId: account.mongoOrganisationId,
    }));
}

/**
 * Resolve a PostgreSQL account by its legacy MongoDB organisation ID.
 */
export async function getAccountByOrganisationId(
  mongoOrganisationId: string,
): Promise<AccountOption | null> {
  const response = await api.get<
    | {
      account: {
        id: string;
        name: string;
        accountNumber: string;
        mongoOrganisationId?: string | null;
      };
    }
    | {
      status: 'pending';
      mongoOrganisationId: string;
      message?: string;
    }
  >(`/v4/accounts/by-mongo-org/${mongoOrganisationId}`);

  if (!('account' in response)) {
    return null;
  }

  if (!response.account.mongoOrganisationId) {
    return null;
  }

  return {
    id: response.account.id,
    name: response.account.name,
    accountNumber: response.account.accountNumber,
    mongoOrganisationId: response.account.mongoOrganisationId,
  };
}

// =============================================================================
// Delete User API Functions
// =============================================================================

/**
 * Remove a user from a specific organisation (soft delete of org profile)
 */
export async function removeUserFromOrganisation(
  pgId: string,
  organisationId: string,
): Promise<User> {
  assertPgId(pgId);
  const response = await api.delete<{ user: User }>(
    `/v4/users/${pgId}/organisations/${organisationId}`,
  );
  return response.user;
}

/**
 * Soft-delete a user (sets enabled=false, deleted_at). PG-only.
 */
export async function deleteUser(pgId: string): Promise<{ id: string; deleted: boolean }> {
  assertPgId(pgId);
  return api.delete<{ id: string; deleted: boolean }>(`/v4/users/${pgId}`);
}

// =============================================================================
// Roles API Functions
// =============================================================================

/**
 * Get all roles
 */
export async function getRoles(): Promise<Role[]> {
  const response = await api.get<{ roles: Role[] }>('/v4/legacy/roles/', {
    pageLength: 100,
  });
  return response.roles || [];
}

// =============================================================================
// Account Security Functions
// =============================================================================

/**
 * Unlock a user's account by resetting login attempts. PG-only — writes
 * incorrect_login_attempts and last_login_attempt directly on the users row.
 */
export async function unlockAccount(pgId: string): Promise<User> {
  assertPgId(pgId);
  const response = await api.put<{ user: User }>(`/v4/users/${pgId}/profile`, {
    incorrect_login_attempts: 0,
    last_login_attempt: null,
  });
  return response.user;
}

export async function updateUserSecurityState(
  userId: string,
  data: UpdateUserSecurityStateInput,
): Promise<User> {
  const response = await api.patch<{ user: User }>(`/v4/users/${userId}/security-state`, data);
  return response.user;
}
